/*
  idam search analytics
  ---------------------
  Sends the four *frontend* interaction events the search backend expects, to
  POST {backend}/api/v1/search/events:

    click              product opened from the search results page
    autocomplete_click suggestion picked in the search overlay
    add_to_cart        product added to cart off the back of a search
    filter             filter applied on the search results page

  `search` / `suggest` / `autocomplete` are recorded server-side on every API
  call, so they are deliberately NOT sent from here.

  Config is read from the "Search (idam)" section root ([data-idam-search]),
  which the header group renders on every page — so this file works site-wide
  even though the widget itself only opens from the header icon.
*/
(function () {
  'use strict';

  /* ----------------------------------------------------------- constants */
  var EVENTS_PATH = '/api/v1/search/events';
  var BATCH_MAX = 50; // hard cap in the API contract
  var BATCH_WAIT_MS = 600; // coalesce non-navigational events
  var FILTER_DEBOUNCE_MS = 400; // price sliders fire change() in bursts
  var RATE_LIMIT_PAUSE_MS = 60 * 1000; // how long a 429 silences sends for
  // The key the backend's own integration guide specifies, so a snippet from
  // their team would share this session rather than starting a second one.
  var SESSION_KEY = 'search_session_id';
  var ATTR_KEY = 'idam-search:attribution';
  var ATTR_TTL_MS = 30 * 60 * 1000;
  var SESSION_ID_MAX = 100;
  var PAGE_URL_MAX = 500;
  var CART_ADD_RE = /\/cart\/add(\.js)?(\?|$)/;

  /* --------------------------------------------------------------- config */
  var cachedConfig;

  function config() {
    if (cachedConfig !== undefined) return cachedConfig;
    var root = document.querySelector('[data-idam-search]');
    var base = root ? (root.getAttribute('data-backend-base-url') || '').replace(/\/$/, '') : '';
    // A missing root or base URL means the section was never configured; a
    // false send-events means the merchant turned tracking off.
    if (!root || !base || root.getAttribute('data-send-events') !== 'true') {
      cachedConfig = null;
    } else {
      cachedConfig = {
        baseUrl: base,
        shopDomain: root.getAttribute('data-shop-domain') || ''
      };
    }
    return cachedConfig;
  }

  var debug = (function () {
    try {
      if (window.location.search.indexOf('idam_debug=1') !== -1) return true;
      return localStorage.getItem('idam-search:debug') === '1';
    } catch (e) { return false; }
  })();

  function log() {
    if (!debug) return;
    console.log.apply(console, ['[idam-analytics]'].concat(Array.prototype.slice.call(arguments)));
  }

  /* ------------------------------------------------------------ utilities */
  function str(value, max) {
    if (value == null) return '';
    var s = String(value).trim();
    return max && s.length > max ? s.slice(0, max) : s;
  }

  function sessionId() {
    try {
      var existing = sessionStorage.getItem(SESSION_KEY);
      if (existing) return str(existing, SESSION_ID_MAX);
      var id = (window.crypto && window.crypto.randomUUID)
        ? window.crypto.randomUUID()
        : 'idam-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
      sessionStorage.setItem(SESSION_KEY, id);
      return id;
    } catch (e) {
      // Private-mode Safari and friends — events are still valid without one.
      return '';
    }
  }

  function pageUrl() {
    return str(window.location.href, PAGE_URL_MAX);
  }

  // Debounces per key, so two different filters applied in quick succession
  // both survive.
  function debounceByKey(fn, wait) {
    var timers = {};
    return function (key) {
      var args = arguments;
      clearTimeout(timers[key]);
      timers[key] = setTimeout(function () { fn.apply(null, args); }, wait);
    };
  }

  /* ---------------------------------------------------------------- queue */
  var queue = [];
  var flushTimer = null;
  var pausedUntil = 0;

  function isPaused() { return Date.now() < pausedUntil; }

  // Guards against a malformed event poisoning a whole batch, since the API
  // validates the batch as a unit.
  var REQUIRED = {
    click: ['query', 'productHandle', 'position'],
    autocomplete_click: ['query', 'selectedValue', 'selectionType'],
    add_to_cart: ['query', 'productHandle'],
    filter: ['query', 'filterKey', 'filterValues']
  };

  function isValid(event) {
    var required = REQUIRED[event && event.eventType];
    if (!required) return false;
    for (var i = 0; i < required.length; i++) {
      var value = event[required[i]];
      if (value == null || value === '') return false;
      if (Array.isArray(value) && !value.length) return false;
    }
    return true;
  }

  function track(event, immediate) {
    if (!config() || !event) return;
    // Don't build a queue that a 429 pause is only going to discard.
    if (isPaused()) return;

    var payload = {};
    for (var key in event) {
      if (Object.prototype.hasOwnProperty.call(event, key) && event[key] != null) {
        payload[key] = event[key];
      }
    }
    var sid = payload.sessionId || sessionId();
    if (sid) payload.sessionId = str(sid, SESSION_ID_MAX);
    else delete payload.sessionId;
    if (!payload.pageUrl) payload.pageUrl = pageUrl();

    if (!isValid(payload)) {
      log('dropped (missing required fields)', payload);
      return;
    }

    log('queued', payload);
    queue.push(payload);

    // `immediate` is for events that are about to unload the page (a click
    // that navigates). Everything else coalesces into one request.
    if (immediate || queue.length >= BATCH_MAX) {
      flush();
    } else if (!flushTimer) {
      flushTimer = setTimeout(flush, BATCH_WAIT_MS);
    }
  }

  function flush() {
    clearTimeout(flushTimer);
    flushTimer = null;
    if (!queue.length) return;
    var pending = queue;
    queue = [];
    while (pending.length) send(pending.splice(0, BATCH_MAX));
  }

  function send(events) {
    var cfg = config();
    if (!cfg || isPaused()) return;
    var body;
    try { body = JSON.stringify({ events: events }); } catch (e) { return; }
    log('POST ' + events.length + ' event(s)', events);
    try {
      // sendBeacon can't set X-Shop-Domain, so it's fetch + keepalive — which
      // survives the navigation a click event triggers.
      fetch(cfg.baseUrl + EVENTS_PATH, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Shop-Domain': cfg.shopDomain },
        body: body,
        keepalive: true
      }).then(handleSendResponse).catch(function () {});
    } catch (e) { /* never let analytics break the page */ }
  }

  function handleSendResponse(response) {
    if (!response || response.ok) return;

    if (response.status === 429) {
      // Rate limited. Back off rather than hammering — dropped analytics is
      // always preferable to piling more load onto a struggling endpoint.
      pausedUntil = Date.now() + RATE_LIMIT_PAUSE_MS;
      log('rate limited (429); pausing sends for ' + (RATE_LIMIT_PAUSE_MS / 1000) + 's');
      return;
    }

    // 400 means the payload is wrong, 401 means X-Shop-Domain is wrong.
    // Neither is worth retrying, but both are worth seeing while debugging.
    if (!debug) return;
    response.json()
      .then(function (data) { log('rejected: HTTP ' + response.status, data && data.message); })
      .catch(function () { log('rejected: HTTP ' + response.status); });
  }

  window.addEventListener('pagehide', flush);
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') flush();
  });

  /* ---------------------------------------------------- search attribution */
  // Remembers which query sent a shopper to a product, so an add-to-cart on
  // the PDP can still be credited to that search.
  function remember(query, productHandle, source) {
    if (!query || !productHandle) return;
    try {
      sessionStorage.setItem(ATTR_KEY, JSON.stringify({
        query: query,
        productHandle: productHandle,
        source: source || '',
        t: Date.now()
      }));
    } catch (e) { /* ignore */ }
  }

  function attributionFor(productHandle) {
    if (!productHandle) return null;
    var record;
    try { record = JSON.parse(sessionStorage.getItem(ATTR_KEY) || 'null'); }
    catch (e) { return null; }
    if (!record || record.productHandle !== productHandle) return null;
    if (Date.now() - (record.t || 0) > ATTR_TTL_MS) return null;
    return record;
  }

  /* -------------------------------------------------- search results page */
  function resultsRoot() {
    return document.querySelector('[data-idam-search-results]');
  }

  // The AJAX filters rewrite the URL but always preserve `q`, so the URL is a
  // more current source than the Liquid-rendered attribute.
  function searchQuery() {
    var q = '';
    try { q = new URLSearchParams(window.location.search).get('q') || ''; }
    catch (e) { q = ''; }
    if (!q) {
      var root = resultsRoot();
      q = root ? root.getAttribute('data-idam-search-query') || '' : '';
    }
    // Shopify's search form can append a wildcard to the raw terms.
    return q.replace(/\*+$/, '').trim();
  }

  function currentPage() {
    try {
      var page = parseInt(new URLSearchParams(window.location.search).get('page'), 10);
      return page > 0 ? page : 1;
    } catch (e) { return 1; }
  }

  function pageSize(root) {
    var size = parseInt(root.getAttribute('data-idam-search-page-size'), 10);
    return size > 0 ? size : 0;
  }

  function bindResultClicks(root) {
    document.addEventListener('click', function (e) {
      var target = e.target;
      if (!target || !target.closest) return;

      // `a.grid-product__link` is the only thing in a card that navigates to
      // the product — anchoring on it keeps quick-add buttons, colour swatches
      // and the quick-shop trigger from counting as product clicks.
      var link = target.closest('a.grid-product__link');
      if (!link || link.hasAttribute('onclick')) return;

      var card = link.closest('.grid-product[data-product-handle]');
      if (!card || !root.contains(card)) return;

      var handle = card.getAttribute('data-product-handle');
      var query = searchQuery();
      if (!handle || !query) return;

      // Siblings only — the grid is the card's own parent, and scoping to it
      // keeps recommendation rails elsewhere on the page out of the count.
      var grid = card.parentElement;
      var cards = grid ? grid.children : [];
      var index = -1;
      for (var i = 0, seen = 0; i < cards.length; i++) {
        if (!cards[i].hasAttribute || !cards[i].hasAttribute('data-product-handle')) continue;
        if (cards[i] === card) { index = seen; break; }
        seen++;
      }

      var size = pageSize(root);
      // Falls back to the on-page index when the page size is unknown, which
      // can only be wrong on page 2+.
      var position = index >= 0 ? (currentPage() - 1) * size + index + 1 : 1;

      remember(query, handle, 'search_results');
      track({
        eventType: 'click',
        query: query,
        productHandle: handle,
        position: position,
        metadata: { source: 'search_results', page: currentPage() }
      }, true);
    }, true);
  }

  /* --------------------------------------------------------------- filters */
  // filter.p.m.custom.sub_category -> sub_category, filter.v.option.color ->
  // color, filter.v.price.gte -> price, filter.p.vendor -> vendor.
  function filterKeyFor(paramName) {
    if (!paramName || paramName.indexOf('filter.') !== 0) return '';
    if (/^filter\.v\.price\./.test(paramName)) return 'price';
    var parts = paramName.split('.');
    return parts[parts.length - 1] || '';
  }

  // The Control and Variation-1 filter forms are both in the DOM (one is only
  // hidden by CSS) and hold independent checked state, so values are always
  // read from the form the shopper actually touched.
  function scopeFor(input) {
    return input.form || (input.closest && input.closest('form')) || document;
  }

  function valuesForParam(scope, paramName) {
    var selector = 'input[name="' + paramName.replace(/"/g, '\\"') + '"]';
    var values = [];
    Array.prototype.forEach.call(scope.querySelectorAll(selector), function (input) {
      var usable = (input.type === 'checkbox' || input.type === 'radio') ? input.checked : true;
      if (usable && input.value && values.indexOf(input.value) === -1) values.push(input.value);
    });
    return values;
  }

  function priceValues(scope) {
    var values = [];
    [['filter.v.price.gte', 'gte'], ['filter.v.price.lte', 'lte']].forEach(function (pair) {
      var input = scope.querySelector('input[name="' + pair[0] + '"]');
      if (input && input.value) values.push(pair[1] + ':' + input.value);
    });
    return values;
  }

  // Values are captured synchronously; only the send is debounced. Applying a
  // filter triggers an AJAX re-render that replaces these inputs, so reading
  // them after the delay would race the new markup.
  var emitFilter = debounceByKey(function (filterKey, query, values) {
    // An empty list means the shopper cleared the last value for this key —
    // that's a removal, not an applied filter.
    if (!values.length) return;
    track({
      eventType: 'filter',
      query: query,
      filterKey: filterKey,
      filterValues: values
    });
  }, FILTER_DEBOUNCE_MS);

  function bindFilters() {
    document.addEventListener('change', function (e) {
      var input = e.target;
      if (!input || !input.name || input.name.indexOf('filter.') !== 0) return;
      var query = searchQuery();
      if (!query) return;
      var key = filterKeyFor(input.name);
      if (!key) return;
      var scope = scopeFor(input);
      emitFilter(key, query, key === 'price' ? priceValues(scope) : valuesForParam(scope, input.name));
    }, true);
  }

  /* ----------------------------------------------------------- add to cart */
  function noteCartAdd(item) {
    if (!item) return;
    if (item.items && item.items.length) {
      Array.prototype.forEach.call(item.items, noteCartAdd);
      return;
    }
    var handle = item.handle || item.product_handle || '';
    if (!handle) return;

    // Added straight from the results grid, or from a PDP the shopper reached
    // through search.
    var query = resultsRoot() ? searchQuery() : '';
    var source = 'search_results';
    if (!query) {
      var record = attributionFor(handle);
      if (!record) return; // not a search-driven add — nothing to report
      query = record.query;
      source = record.source || 'attributed';
    }
    if (!query) return;

    track({
      eventType: 'add_to_cart',
      query: query,
      productHandle: handle,
      metadata: { source: source }
    });
  }

  // Every add-to-cart path in this theme (PDP form, quick shop, grid quick
  // add) goes through fetch(theme.routes.cartAdd), so one patch covers them
  // all — the theme's own `ajaxProduct:added` event is skipped on some paths.
  function patchCartAdd() {
    if (!window.fetch || window.__idamCartAddPatched) return;
    window.__idamCartAddPatched = true;
    var originalFetch = window.fetch;

    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var method = (init && init.method) || (input && input.method) || 'GET';
      var promise = originalFetch.apply(this, arguments);

      if (CART_ADD_RE.test(url) && String(method).toUpperCase() === 'POST') {
        promise.then(function (response) {
          if (!response || !response.ok) return;
          // Cloned before the caller reads the body — this handler is attached
          // first, so the clone always happens while the body is untouched.
          response.clone().json().then(function (data) {
            if (data && data.status && data.status !== 200) return; // 422 etc.
            noteCartAdd(data);
          }).catch(function () {});
        }).catch(function () {});
      }

      return promise;
    };
  }

  /* ------------------------------------------ server-side search logging */
  /*
    Shopify's own search renders the results page, so the backend never sees
    the query and can't record the `search` event that feeds top_queries,
    zero_result_queries, search_volume and the device/browser breakdowns.
    Mirroring the shopper's query to /api/v1/search/products is what makes the
    backend log it; the response is deliberately discarded — Liquid still
    renders the grid, keeping the filter sidebar and product cards intact.
  */
  var SEARCH_PATH = '/api/v1/search/products';

  // The backend rejects anything outside its enum with a 400, and a rejected
  // request logs nothing — so unmapped Shopify sorts (best-selling,
  // title-descending, created-ascending, manual) fall back to relevance.
  var SORT_MAP = {
    'relevance': 'relevance',
    'price-ascending': 'price_asc',
    'price-descending': 'price_desc',
    'title-ascending': 'title_asc',
    'created-descending': 'newest'
  };

  var loggedSearches = {};

  function logSearch() {
    var cfg = config();
    var root = resultsRoot();
    if (!cfg || !root) return;

    var query = searchQuery();
    if (!query) return;

    var params;
    try { params = new URLSearchParams(window.location.search); }
    catch (e) { return; }

    var sort = SORT_MAP[params.get('sort_by') || 'relevance'] || 'relevance';
    var page = currentPage();
    var limit = pageSize(root) || 20;

    // Applying a filter rewrites the URL without changing the query, and the
    // backend has no filter support — re-firing there would inflate volume.
    var key = query + '|' + page + '|' + sort;
    if (loggedSearches[key]) return;
    loggedSearches[key] = true;

    var url = cfg.baseUrl + SEARCH_PATH
      + '?q=' + encodeURIComponent(query)
      + '&page=' + page
      + '&limit=' + limit
      + '&sort=' + encodeURIComponent(sort);

    log('logging search', url);
    try {
      fetch(url, {
        method: 'GET',
        headers: { 'X-Shop-Domain': cfg.shopDomain, 'Accept': 'application/json' }
      }).catch(function () {});
    } catch (e) { /* never let analytics break the page */ }
  }

  // Off the critical path — the grid is already on screen from Liquid.
  function logSearchWhenIdle() {
    if (window.requestIdleCallback) window.requestIdleCallback(logSearch, { timeout: 3000 });
    else setTimeout(logSearch, 800);
  }

  // Sorting and filtering re-render the grid over AJAX and pushState rather
  // than reloading, so a sort change would otherwise never be logged.
  function watchUrlChanges(onChange) {
    var history = window.history;
    if (!history || window.__idamHistoryPatched) {
      window.addEventListener('popstate', onChange);
      return;
    }
    window.__idamHistoryPatched = true;
    ['pushState', 'replaceState'].forEach(function (name) {
      var original = history[name];
      if (typeof original !== 'function') return;
      history[name] = function () {
        var result = original.apply(this, arguments);
        try { onChange(); } catch (e) { /* ignore */ }
        return result;
      };
    });
    window.addEventListener('popstate', onChange);
  }

  /* ------------------------------------------------------------ public API */
  window.IdamSearchAnalytics = {
    track: track,
    flush: flush,
    sessionId: sessionId,
    searchQuery: searchQuery,
    logSearch: logSearch,
    rememberSearchClick: remember,
    isEnabled: function () { return !!config(); }
  };

  /* ------------------------------------------------------------------ boot */
  function boot() {
    if (!config()) {
      log('disabled (no backend URL, or "Send search analytics events" is off)');
      return;
    }
    patchCartAdd();
    bindFilters();
    var root = resultsRoot();
    if (root) {
      bindResultClicks(root);
      logSearchWhenIdle();
      watchUrlChanges(logSearchWhenIdle);
    }
    log('ready', { sessionId: sessionId(), searchQuery: searchQuery() });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
